import { Box, ButtonGroup } from "@mui/material"
import type { ReactElement } from "react"
import { useState } from "react"
import { twMerge } from "tailwind-merge"

import { Button } from "@/components/common/Button"
import { SectionContainer } from "@/components/SectionContainer/SectionContainer"

interface PageTabsProps<T> {
  tabs: T[]
  renderContent: (tab: T) => JSX.Element
  renderSideContent?: (tab: T) => JSX.Element | null
  // Hides the tab buttons when the tab label is already shown elsewhere; the side content stays.
  hideTabs?: boolean
}

const PageTabs = <T,>({ tabs, renderContent, renderSideContent, hideTabs = false }: PageTabsProps<T>): ReactElement => {
  const [tab, settab] = useState<T>(tabs[0])

  return (
    <SectionContainer className='flex flex-col gap-4 overflow-hidden'>
      <Box className={twMerge("flex items-center justify-between", hideTabs && "justify-end")}>
        <ButtonGroup
          color='inherit'
          orientation='horizontal'
          variant='outlined'
          className={twMerge("border border-gray-400 bg-white", hideTabs && "hidden")}
        >
          {tabs.map((tabItem) => (
            <Button
              key={String(tabItem)}
              onClick={() => settab(tabItem)}
              className={twMerge([
                "h-9 rounded-none rounded-l-sm border-gray-400 uppercase text-gray-700",
                tab === tabItem ? "bg-gray-300" : "",
              ])}
              appearance='text'
            >
              {String(tabItem)}
            </Button>
          ))}
        </ButtonGroup>
        {renderSideContent?.(tab)}
      </Box>
      {renderContent(tab)}
    </SectionContainer>
  )
}

export default PageTabs
